import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { entitlementValueType, renderUsage, UNLIMITED } from './usageStatus';
import { commercialError } from './useCommercialApi';
import { buildGovernancePayload } from './governancePayload';
import { AlertTriangle, CheckCircle2, ShieldCheck } from 'lucide-react';

/**
 * SACP — type-safe owner Grant / Deny / numeric-override governance modal.
 *
 * Replaces the removed native window.prompt() flow. The FORM SHAPE is driven by the capability's
 * backend value type (entitlementValueType): a Boolean capability never shows a numeric field, and a
 * numeric limit never shows a bare grant/deny. Every submit calls the REAL mounted owner endpoint
 * POST /super-admin/commercial/tenants/{tenantRef}/overrides (or /trial/extend), which requires a
 * reason + commercial basis, persists, bumps the entitlement version and writes an audit row.
 *
 * Honesty constraints:
 *  - The overrides endpoint applies IMMEDIATELY and accepts `expiry` but not a future effective date,
 *    so no effective-date input is shown for overrides (it would be silently ignored). Expiry is real.
 *  - commercial_basis is required by the backend (complimentary|promotional|contract|checkout_required).
 *  - Unlimited (-1) is offered for a numeric limit only when the caller explicitly selects it.
 *  - No control is shown that lacks a real backend endpoint (Pack/add-on assignment has none here and
 *    is surfaced as unavailable rather than as a dead submit).
 */

const COMMERCIAL_BASIS = [
  { value: 'complimentary', label: 'Complimentary (no charge)' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'contract', label: 'Contract' },
  { value: 'checkout_required', label: 'Checkout required (no charge here)' },
];

const FieldLabel = ({ children, required }) => (
  <label className="block text-xs font-semibold text-gray-600 mb-1">
    {children}{required && <span className="text-red-500"> *</span>}
  </label>
);

const ReadRow = ({ k, v }) => (
  <div className="flex justify-between py-1 text-sm border-b border-gray-100 last:border-0">
    <span className="text-gray-500">{k}</span><span className="text-gray-900 font-medium">{v}</span>
  </div>
);

/**
 * @param mode       'boolean' | 'numeric' | 'trial' | 'beta'  (chosen by the caller from the record type)
 * @param betaAction 'grant' | 'revoke'  (mode 'beta' only)
 * @param record    the feature/limit/tenant record being governed
 * @param usage     canonical usage metric for a numeric limit (optional, for context)
 */
export default function OwnerGovernanceModal({ open, onClose, api, tenantRef, tenantName, mode, record, usage, onDone,
                                              incumbentOverrideId, betaAction }) {
  // PRIVATE BETA is a THIRD kind of owner decision, not a variant of the entitlement override. It has no
  // commercial basis, no allowance and no expiry: it answers "may this tenant reach an unreleased
  // capability at all", which is gate 3, while grant/deny answers gate 4. Sharing this modal keeps ONE
  // reason-required governance flow; sharing the FORM would imply the two decisions are the same one.
  const isBeta = mode === 'beta';
  const commercialForm = mode !== 'trial' && !isBeta;
  const valueType = useMemo(() => (mode === 'numeric' ? 'numeric_limit' : entitlementValueType(record)), [mode, record]);
  const featureKey = record?.feature_key || record?.key || record?.limit_key || '';
  const featureName = record?.feature_display_name || record?.display_name || featureKey;

  // shared fields
  const [decision, setDecision] = useState('grant');        // grant | deny  (boolean/configurable)
  const [reason, setReason] = useState('');
  const [basis, setBasis] = useState('complimentary');
  const [expiry, setExpiry] = useState('');                 // optional ISO date (real: backend `expiry`)
  // numeric fields
  const [allowanceMode, setAllowanceMode] = useState('value'); // value | unlimited
  const [allowance, setAllowance] = useState('');
  // trial fields
  const [days, setDays] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);

  const currentAllowance = usage ? (usage.limit === UNLIMITED ? 'Unlimited' : usage.limit) : (record?.value_label ?? '—');
  const currentUsageText = usage ? renderUsage(usage) : null;

  const reset = () => { setReason(''); setBasis('complimentary'); setExpiry(''); setAllowance(''); setAllowanceMode('value'); setDays(''); setErr(null); setOk(null); };
  const close = () => { reset(); onClose && onClose(); };

  const validate = () => {
    if (!reason.trim()) return 'A reason is required.';
    if (isBeta) {
      if (betaAction !== 'grant' && betaAction !== 'revoke') return 'No private-beta action was selected.';
      return null;                       // reason is the ONLY input a beta decision takes
    }
    if (mode === 'trial') {
      const d = Number(days);
      if (!Number.isInteger(d) || d <= 0) return 'Enter a whole number of days greater than 0.';
      return null;
    }
    if (!basis) return 'A commercial basis is required.';
    if (valueType === 'numeric_limit' && allowanceMode === 'value') {
      const n = Number(allowance);
      if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) return 'Enter a whole, non-negative allowance (or choose Unlimited).';
    }
    return null;
  };

  const resultingAllowance = () => {
    if (valueType !== 'numeric_limit') return null;
    if (allowanceMode === 'unlimited') return 'Unlimited';
    return allowance === '' ? '—' : Number(allowance).toLocaleString();
  };

  const submit = async () => {
    const v = validate();
    if (v) { setErr(v); return; }
    setSubmitting(true); setErr(null); setOk(null);
    try {
      // `incumbentOverrideId` routes a numeric limit that ALREADY has an active override through the atomic
      // replace primitive instead of creating a second active authority. Absent => unchanged create path.
      const { path, body } = buildGovernancePayload({
        mode, valueType, tenantRef, decision, featureKey, reason, basis, expiry, allowanceMode, allowance, days,
        incumbentOverrideId, betaAction,
      });
      const res = await api.post(path, body);
      const ver = res?.data?.entitlement_version;
      setOk(`Saved. Audit recorded${ver != null ? ` · entitlement version ${ver}` : ''}.`);
      // Let the parent re-read authoritative effective state, then close shortly after.
      if (onDone) await onDone(res?.data);
      setTimeout(close, 700);
    } catch (e) {
      // A 409 OVERRIDE_REPLACEMENT_CONFLICT means another owner changed this limit first. NEVER fall back to
      // create (that would leave two active authorities) and never re-submit over the newer state — surface it
      // truthfully and make the owner refresh so they decide against current truth.
      const code = e?.response?.data?.detail?.code || e?.response?.data?.code;
      if (e?.response?.status === 409 && code === 'OVERRIDE_REPLACEMENT_CONFLICT') {
        setErr('This limit was changed by someone else while this dialog was open. Refresh and review the '
               + 'current value before applying a new one.');
      } else {
        setErr(commercialError(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === 'trial' ? 'Extend trial'
    : isBeta ? `${betaAction === 'revoke' ? 'Revoke' : 'Grant'} private beta — ${featureName}`
    : valueType === 'numeric_limit' ? `Override numeric limit — ${featureName}`
    : `Grant / Deny — ${featureName}`;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-purple-600" />{title}</DialogTitle>
          <DialogDescription>
            {tenantName ? <>Tenant: <span className="font-medium">{tenantName}</span></> : null}
            {/* Say what is ACTUALLY recorded. A beta decision (and a trial extension) takes no commercial
                basis, so promising one in the audit log is a claim the backend does not honour. */}
            <span className="block mt-1 text-xs text-gray-500">
              Applies immediately. {commercialForm ? 'A reason and commercial basis are' : 'A reason is'} recorded in the audit log.
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* context (read-only, authoritative) */}
        <div className="rounded border bg-gray-50 px-3 py-2 mb-3">
          <ReadRow k="Capability" v={featureName} />
          {commercialForm && <ReadRow k="Type" v={valueType === 'numeric_limit' ? 'Numeric limit' : (valueType === 'configurable' ? 'Configurable capability' : 'Boolean capability')} />}
          {record?.business_plane_state && <ReadRow k="Current state" v={record.business_plane_state} />}
          {/* The three gates, side by side and never collapsed: a release state is not a grant, and a
              beta grant is not an entitlement. Read straight from the backend projection. */}
          {isBeta && <ReadRow k="Release state" v={record?.release_state || '—'} />}
          {isBeta && <ReadRow k="Private beta access" v={record?.beta_entitled ? 'Granted' : 'Not granted'} />}
          {isBeta && <ReadRow k="Tenant entitlement" v={record?.entitled ? 'Granted' : 'Not granted'} />}
          {valueType === 'numeric_limit' && <ReadRow k="Current allowance" v={String(currentAllowance)} />}
          {currentUsageText && <ReadRow k="Current usage" v={currentUsageText} />}
        </div>

        <div className="space-y-3">
          {/* decision (boolean/configurable) */}
          {commercialForm && valueType !== 'numeric_limit' && (
            <div>
              <FieldLabel required>Decision</FieldLabel>
              <div className="flex gap-2">
                {['grant', 'deny'].map((d) => (
                  <button key={d} type="button" onClick={() => setDecision(d)}
                    className={`px-3 py-1.5 rounded text-sm border ${decision === d ? (d === 'grant' ? 'bg-green-600 text-white border-green-600' : 'bg-red-600 text-white border-red-600') : 'bg-white text-gray-700 border-gray-300'}`}>
                    {d === 'grant' ? 'Grant' : 'Deny'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* numeric allowance */}
          {commercialForm && valueType === 'numeric_limit' && (
            <div>
              <FieldLabel required>New allowance</FieldLabel>
              <div className="flex items-center gap-2">
                <Select value={allowanceMode} onValueChange={setAllowanceMode}>
                  <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="value">Set a number</SelectItem>
                    <SelectItem value="unlimited">Unlimited</SelectItem>
                  </SelectContent>
                </Select>
                {allowanceMode === 'value' && (
                  <input type="number" min="0" step="1" value={allowance} onChange={(e) => setAllowance(e.target.value)}
                    className="flex-1 border rounded px-2 py-1.5 text-sm" placeholder="e.g. 130" />
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Resulting allowance: <span className="font-medium">{resultingAllowance()}</span></p>
            </div>
          )}

          {/* trial days */}
          {mode === 'trial' && (
            <div>
              <FieldLabel required>Extend by (days)</FieldLabel>
              <input type="number" min="1" step="1" value={days} onChange={(e) => setDays(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm" placeholder="e.g. 14" />
            </div>
          )}

          {/* commercial basis — not for trial, and NOT for beta (beta access is not a commercial sale) */}
          {commercialForm && (
            <div>
              <FieldLabel required>Commercial basis</FieldLabel>
              <Select value={basis} onValueChange={setBasis}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMMERCIAL_BASIS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* expiry (optional, real backend field). The beta routes accept no expiry, so showing one
              here would be a control that silently does nothing. */}
          {commercialForm && (
            <div>
              <FieldLabel>Expiry (optional)</FieldLabel>
              <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm" />
            </div>
          )}

          {/* What this decision does NOT do. Granting beta on a feature the tenant is not entitled to
              leaves it LOCKED, and revoking beta leaves a stored entitlement untouched — saying so here
              is what stops an owner reading one successful action as "the feature is now on". */}
          {isBeta && (
            <div className="rounded border border-purple-200 bg-purple-50 px-3 py-2 text-xs text-purple-900">
              {betaAction === 'grant'
                ? 'Private beta access clears the release gate for this tenant only. It does not entitle the tenant: the feature stays unavailable until the tenant feature entitlement is also granted.'
                : 'Revoking private beta blocks this tenant at the release gate again. Any commercial entitlement stays exactly as it is, and other tenants are unaffected.'}
            </div>
          )}

          {/* reason (required everywhere) */}
          <div>
            <FieldLabel required>Reason (recorded in audit)</FieldLabel>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Why is this change being made?" />
          </div>

          {err && <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"><AlertTriangle className="h-4 w-4" />{err}</div>}
          {ok && <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2"><CheckCircle2 className="h-4 w-4" />{ok}</div>}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={close} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? 'Saving…'
              : mode === 'trial' ? 'Extend trial'
              : isBeta ? (betaAction === 'revoke' ? 'Revoke private beta' : 'Grant private beta')
              : valueType === 'numeric_limit' ? 'Set allowance'
              : (decision === 'grant' ? 'Grant capability' : 'Deny capability')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
